import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

// Google Places API (New) endpoints. The same REACT_APP_MAP_GOOGLE key already
// used for the Maps Embed preview works here once "Places API (New)" is enabled
// on the project. If the API is disabled/unreachable the component silently
// degrades to a plain free-text address field, so signup is never blocked.
const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const DETAILS_URL = 'https://places.googleapis.com/v1/places';
const API_KEY = process.env.REACT_APP_MAP_GOOGLE;

// Pull the fields the address form needs out of a Place Details response.
function parseAddressComponents(components = []) {
  const get = (type) => components.find((c) => (c.types || []).includes(type));
  const streetNumber = get('street_number')?.longText || '';
  const route = get('route')?.longText || '';
  return {
    address: [streetNumber, route].filter(Boolean).join(' ').trim(),
    // locality is the usual city; fall back to sublocality/postal town.
    city: get('locality')?.longText || get('postal_town')?.longText || get('sublocality')?.longText || '',
    // Store the full state name (e.g. "Kansas") to match the master state list.
    state: get('administrative_area_level_1')?.longText || '',
    // Two-letter code (e.g. "KS") for forms that track state abbreviation.
    stateCode: get('administrative_area_level_1')?.shortText || '',
    zipCode: get('postal_code')?.longText || ''
  };
}

AddressAutocomplete.propTypes = {
  // Called with { address, city, state, zipCode } when a suggestion is picked.
  onSelect: PropTypes.func.isRequired,
  // Current street value from the parent form (keeps the field controlled).
  value: PropTypes.string,
  // Fired on free typing so the parent can keep its own `address` field in sync.
  onInputChange: PropTypes.func,
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool
};

/**
 * Address field backed by Google Places Autocomplete. Selecting a suggestion
 * fills the caller's structured address fields and flags the address verified
 * (cards 152 / 184). Falls back to a normal text field when Places is
 * unavailable.
 */
export default function AddressAutocomplete({
  onSelect,
  value = '',
  onInputChange,
  label = 'Address',
  error,
  helperText,
  disabled
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchPredictions = (input) => {
    if (!API_KEY || !input || input.length < 3) {
      setOptions([]);
      return;
    }
    setLoading(true);
    fetch(AUTOCOMPLETE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': API_KEY },
      body: JSON.stringify({ input, includedRegionCodes: ['us'] })
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        const suggestions = (data.suggestions || [])
          .map((s) => s.placePrediction)
          .filter(Boolean)
          .map((p) => ({ placeId: p.placeId, description: p.text?.text || '' }));
        setOptions(suggestions);
      })
      .catch(() => setOptions([])) // API disabled/blocked → behave as plain field
      .finally(() => setLoading(false));
  };

  const handleInputChange = (event, newInput, reason) => {
    if (reason === 'reset') return; // selection, not typing
    if (onInputChange) onInputChange(newInput);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(newInput), 300);
  };

  const handleChange = (event, option) => {
    if (!option || typeof option === 'string' || !option.placeId) return;
    fetch(`${DETAILS_URL}/${option.placeId}`, {
      headers: {
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': 'addressComponents,formattedAddress'
      }
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((place) => {
        const parsed = parseAddressComponents(place.addressComponents);
        onSelect({ ...parsed, isVerified: true });
      })
      .catch(() => {
        // Details lookup failed — keep whatever the user typed, unverified.
        onSelect({ address: option.description, isVerified: false });
      });
  };

  return (
    <Autocomplete
      fullWidth
      freeSolo
      autoComplete
      includeInputInList
      filterOptions={(x) => x}
      options={options}
      loading={loading}
      disabled={disabled}
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      isOptionEqualToValue={(option, val) => option.placeId === val.placeId}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
}
